import type { ConditionSafetyState } from "@/lib/conditions/safety-notes";
import type { FactorConfidence } from "@/lib/conditions/factor-explanations";
import type { Ability } from "@/lib/session-planner/types";

export const COMPANION_RECOMMENDATION_SPORTS = [
  "piste",
  "off-piste",
  "touring",
  "mtb",
  "surf",
  "bodyboarding",
  "wind",
  "foil",
  "downwind-foil",
  "wing-foil",
  "diving",
  "sup",
  "sup-touring",
  "sup-surf",
  "sup-downwind",
  "swim",
  "sailing-club",
  "sailing-hp",
] as const;

export type CompanionRecommendationSport =
  (typeof COMPANION_RECOMMENDATION_SPORTS)[number];

export function normaliseCompanionSportPreference(
  value: string | null | undefined,
): CompanionRecommendationSport | null {
  const key = value?.trim().toLowerCase().replaceAll("_", "-");
  if (!key) return null;
  const normalised = key === "sup" ? "sup-touring" : key;
  return (COMPANION_RECOMMENDATION_SPORTS as readonly string[]).includes(normalised)
    ? normalised as CompanionRecommendationSport
    : null;
}

export type CompanionSportEvidenceSignal =
  | "explicit-known"
  | "call-follow"
  | "planner-preference"
  | "tracked-session"
  | "manual-session";

export type CompanionSportEvidence = {
  sportKey: CompanionRecommendationSport;
  known: true;
  ability: Ability | null;
  signals: CompanionSportEvidenceSignal[];
};

export type CompanionBeginnerSuitability =
  | "beginner-suitable"
  | "experienced-only"
  | "unknown"
  | "safety-restricted";

export type CompanionRecommendationCandidate = {
  id: string;
  locationId: string;
  slug: string;
  placeName: string;
  timezone?: string;
  region: string | null;
  sportKey: CompanionRecommendationSport;
  score: number;
  verdict: string | null;
  remainingWindow: string | null;
  remainingWindowStartAt: string | null;
  remainingWindowEndAt: string | null;
  nextWindow: string | null;
  nextWindowStartAt: string | null;
  nextWindowEndAt: string | null;
  updatedAt: string | null;
  safetyState: ConditionSafetyState;
  safetyNotes: string[];
  /** Producer-owned score evidence, independent of freshness/window confidence. */
  scoreConfidence: FactorConfidence;
  scoreConfidenceReasons: string[];
  /** Freshness and remaining-window confidence used by recommendation ranking. */
  confidence: "high" | "medium" | "low";
  abilityCompatibility: "compatible" | "incompatible" | "unknown";
  beginnerSuitability: CompanionBeginnerSuitability;
  distanceKm: number;
  driveMinutes: number;
  withinTravelCap: boolean;
  fitsAvailability: boolean | null;
  callSupported: boolean;
  briefingSupported: boolean;
  plannerHref: string | null;
  conditionsHref: string;
  isSaved: boolean;
};

export type CompanionRecommendationExplanation = {
  whySport: string;
  whyPlace: string;
  whyTime: string;
  whatCouldChange: string;
};

export type CompanionRankedRecommendation = {
  candidate: CompanionRecommendationCandidate;
  explanation: CompanionRecommendationExplanation;
  rankScore: number;
};

export type CompanionDiscoveryRecommendation = CompanionRankedRecommendation & {
  suitability: Exclude<CompanionBeginnerSuitability, "safety-restricted">;
};

export type CompanionCrossSportResult = {
  bestForYou: CompanionRankedRecommendation | null;
  alsoFiringNearby: CompanionDiscoveryRecommendation | null;
  emptyReason?: CompanionRecommendationEmptyReason | null;
};

export type CompanionRecommendationEmptyReason =
  | "no-sports"
  | "no-base"
  | "missing-data"
  | "stale-data"
  | "no-window"
  | "safety"
  | "safety-unknown"
  | "ability"
  | "availability"
  | "travel";
